import { useEffect, useState } from "react";
import type React from "react";
import {
  AlertTriangle,
  ArrowUp,
  CalendarClock,
  FileText,
  History,
  Info,
  Printer,
  ShieldCheck,
} from "lucide-react";
import { LEGAL_EFFECTIVE_DATE, PRIVACY_UPDATED_DATE, siteConfig, TERMS_VERSION } from "./config";

/* ------------------------------------------------------------------ */
/* Shared legal-page primitives                                        */
/* ------------------------------------------------------------------ */

const SUPPORT = siteConfig.supportEmail;
const SUPPORT_LINK = (
  <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
);

interface LegalSectionDef {
  id: string;
  group: string;
  title: string;
  body: React.ReactNode;
}

function Callout({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warn";
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`legal-callout ${tone}`} role={tone === "warn" ? "note" : undefined}>
      <span className="legal-callout-icon" aria-hidden="true">
        {tone === "warn" ? <AlertTriangle size={18} /> : <Info size={18} />}
      </span>
      <div className="legal-callout-body">
        {title && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 640);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      className={`legal-totop ${show ? "is-visible" : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
    >
      <ArrowUp size={18} />
    </button>
  );
}

function groupSections(sections: LegalSectionDef[]) {
  const groups: { name: string; items: { def: LegalSectionDef; index: number }[] }[] = [];
  sections.forEach((def, index) => {
    let g = groups.find((x) => x.name === def.group);
    if (!g) {
      g = { name: def.group, items: [] };
      groups.push(g);
    }
    g.items.push({ def, index });
  });
  return groups;
}

function tocClick(event: React.MouseEvent<HTMLAnchorElement>, id: string) {
  event.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
}

function TocNav({ groups }: { groups: ReturnType<typeof groupSections> }) {
  return (
    <nav aria-label="Table of contents">
      {groups.map((group) => (
        <div className="legal-toc-group" key={group.name}>
          <span className="legal-toc-group-label">{group.name}</span>
          <ol>
            {group.items.map(({ def, index }) => (
              <li key={def.id}>
                <a href={`#${def.id}`} onClick={(event) => tocClick(event, def.id)}>
                  <span className="legal-toc-num">{String(index + 1).padStart(2, "0")}</span>
                  {def.title}
                </a>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </nav>
  );
}

function LegalLayout({
  eyebrow,
  title,
  summary,
  icon,
  intro,
  sections,
  updated,
}: {
  eyebrow: string;
  title: string;
  summary: string;
  icon: React.ReactNode;
  intro: React.ReactNode;
  sections: LegalSectionDef[];
  updated?: string;
}) {
  const groups = groupSections(sections);

  return (
    <section className="legal page-section">
      <div className="shell legal-hero">
        <span className="icon-badge">{icon}</span>
        <p className="eyebrow eyebrow-pill">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{summary}</p>
        <div className="legal-meta">
          <span className="legal-meta-pill">
            <CalendarClock size={15} aria-hidden="true" /> Effective {LEGAL_EFFECTIVE_DATE}
          </span>
          {updated && (
            <span className="legal-meta-pill">
              <CalendarClock size={15} aria-hidden="true" /> Updated {updated}
            </span>
          )}
          <span className="legal-meta-pill">
            <History size={15} aria-hidden="true" /> Version {TERMS_VERSION}
          </span>
          <button type="button" className="legal-meta-pill legal-print" onClick={() => window.print()}>
            <Printer size={15} aria-hidden="true" /> Print or save as PDF
          </button>
        </div>
      </div>

      <div className="shell legal-body">
        <aside className="legal-toc" aria-label="On this page">
          <div className="legal-toc-inner">
            <p className="legal-toc-title">On this page</p>
            <TocNav groups={groups} />
          </div>
        </aside>

        <article className="legal-doc">
          <details className="legal-toc-mobile">
            <summary>On this page</summary>
            <TocNav groups={groups} />
          </details>

          {intro}

          {sections.map((def, index) => (
            <section className="legal-section" id={def.id} key={def.id}>
              <h2>
                <span className="legal-section-num" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                {def.title}
              </h2>
              {def.body}
            </section>
          ))}
        </article>
      </div>

      <BackToTop />
    </section>
  );
}

/* ================================================================== */
/* TERMS OF SERVICE                                                    */
/* ================================================================== */

const termsSections: LegalSectionDef[] = [
  /* ---- The basics ---- */
  {
    id: "agreement",
    group: "The basics",
    title: "Agreement to these Terms",
    body: (
      <>
        <p>
          These Terms of Service (the “Terms”) form a binding agreement between you and FileAgent (“FileAgent,” “we,” “us,” or
          “our”), the provider of the FileAgent self-hosted file transfer automation software, the FileAgent website, and the
          related licensing, purchase, and support services (together, the “Services”). “FileAgent” is operated as a trade name
          (doing business as “FileAgent”).
        </p>
        <p>
          By downloading, installing, accessing, activating, purchasing, or otherwise using the Services, you agree to these
          Terms and to our <a href="/privacy">Privacy Policy</a>, which is incorporated by reference. If you do not agree, do not
          download, install, or use the Services.
        </p>
        <p>
          These Terms apply to self-serve use of the Services. If you and FileAgent have signed a separate written agreement,
          order form, or enterprise agreement covering the Services, that document governs to the extent it conflicts with these
          Terms (see <a href="#precedence" onClick={(e) => tocClick(e, "precedence")}>Order of precedence</a>).
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    group: "The basics",
    title: "Eligibility and authority",
    body: (
      <>
        <p>
          You must be at least 18 years old to use the Services. The Services are intended for business and professional use and
          are not directed to children.
        </p>
        <p>
          If you use the Services on behalf of a company, organization, or other legal entity (an “Organization”), you represent
          and warrant that you are authorized to bind that Organization to these Terms, and “you” refers to that Organization. You
          are responsible for ensuring that everyone who uses the Services under your account or license complies with these
          Terms.
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    group: "The basics",
    title: "Accounts, administrators, and security",
    body: (
      <>
        <p>
          Some parts of the Services require account, billing, or license information. You agree to provide accurate, current, and
          complete information and to keep it up to date. Because FileAgent is self-hosted, you create and control the user
          accounts, roles, and administrative settings inside your own FileAgent installation.
        </p>
        <p>You are responsible, at all times, for:</p>
        <ul className="legal-list">
          <li>safeguarding your license keys, account credentials, administrator accounts, and any access to your installation;</li>
          <li>all activity that occurs under your license, accounts, and installation, whether or not authorized by you;</li>
          <li>configuring roles, permissions, and access controls appropriately for the people who use FileAgent; and</li>
          <li>promptly notifying us at {SUPPORT_LINK} if you suspect unauthorized use of your license or account.</li>
        </ul>
        <p>
          Administrators hold elevated privileges, including the ability to create jobs that move or permanently delete files. You
          are responsible for limiting administrator access to trusted, qualified personnel.
        </p>
      </>
    ),
  },

  /* ---- License & plans ---- */
  {
    id: "license-grant",
    group: "License and plans",
    title: "License grant",
    body: (
      <>
        <p>
          Subject to your compliance with these Terms and payment of all applicable fees, FileAgent grants you a limited,
          non-exclusive, non-transferable, non-sublicensable, revocable license to install and use the FileAgent software on
          hosts you own or control, for your own internal business purposes, in accordance with your active plan and any usage
          limits associated with it.
        </p>
        <p>
          The software is licensed, not sold. We and our licensors retain all right, title, and interest in and to the Services.
          You receive only the rights expressly granted in these Terms.
        </p>
      </>
    ),
  },
  {
    id: "license-restrictions",
    group: "License and plans",
    title: "License restrictions",
    body: (
      <>
        <p>Except as expressly permitted in writing by FileAgent, you may not, and may not permit anyone else to:</p>
        <ul className="legal-list">
          <li>copy, modify, distribute, sell, resell, rent, lease, sublicense, or provide the Services to third parties as a service bureau or hosted offering;</li>
          <li>reverse engineer, decompile, or disassemble the software, or attempt to derive its source code, except to the limited extent applicable law expressly permits despite this restriction;</li>
          <li>circumvent, disable, or tamper with license enforcement, plan limits, feature gating, activation, or update mechanisms;</li>
          <li>exceed the user, job, credential, connector, or other limits of your plan, or share a single license across more environments or entities than your plan permits;</li>
          <li>remove, alter, or obscure any proprietary notices, branding, or labels;</li>
          <li>use the Services to build or benchmark a competing product, or publish performance results, without our prior written consent; or</li>
          <li>use the Services in violation of these Terms or applicable law.</li>
        </ul>
      </>
    ),
  },
  {
    id: "plans",
    group: "License and plans",
    title: "Plans, features, and license enforcement",
    body: (
      <>
        <p>
          The Services are offered through tiered plans (including a Free plan and paid plans) with different connectors,
          operations, limits, and features, as described on our <a href="/pricing">pricing page</a> at the time of your purchase.
          Paid connectors, higher limits, advanced operations, alerts, administrative controls, and other paid features require a
          valid, active license.
        </p>
        <p>
          The Free plan is provided for evaluation and limited use and is subject to the limits described for that plan. Plan
          features and limits may change over time; we will not reduce a paid feature you are actively entitled to during a paid
          term in a way that materially deprives you of that feature, except as required for security, legal, or technical
          reasons.
        </p>
        <p>
          To enforce licensing and deliver updates, FileAgent performs periodic license validation and update checks over an
          encrypted (HTTPS) connection. If a license is invalid, expired, exceeded, suspended, or cannot be validated within any
          applicable grace period, paid features may be limited or disabled. You agree not to interfere with these checks.
        </p>
      </>
    ),
  },
  {
    id: "beta",
    group: "License and plans",
    title: "Beta, preview, and experimental features",
    body: (
      <p>
        We may offer features identified as beta, preview, early access, trial, or experimental (“Beta Features”). Beta Features
        are provided “as is,” may be incomplete or unstable, may change or be withdrawn at any time, and are excluded from any
        support commitments and service expectations. You use Beta Features at your own risk and should not rely on them for
        production-critical workflows.
      </p>
    ),
  },

  /* ---- Billing ---- */
  {
    id: "fees",
    group: "Billing and subscriptions",
    title: "Fees, billing, and payment",
    body: (
      <>
        <p>
          Paid plans are sold on a subscription basis (monthly or annual) at the prices listed at the time of purchase. Payments
          are processed by our third-party payment processor, Stripe. By purchasing a paid plan, you authorize us and Stripe to
          charge your selected payment method for the applicable fees, including recurring charges, until you cancel.
        </p>
        <p>
          You are responsible for providing valid, current payment information and for keeping it up to date. We do not store
          your full payment card number; card data is handled by Stripe under its own terms and security practices.
        </p>
      </>
    ),
  },
  {
    id: "renewals",
    group: "Billing and subscriptions",
    title: "Renewals, cancellations, upgrades, and downgrades",
    body: (
      <>
        <p>
          Unless otherwise stated, paid subscriptions automatically renew at the end of each billing period (monthly or annual)
          at the then-current price for your plan, and your payment method is charged for the renewal until you cancel. You
          authorize these recurring charges.
        </p>
        <p>
          You may cancel at any time, effective at the end of your current paid period. After cancellation, your plan continues
          until the end of the period you have already paid for, and then your paid features stop. Upgrades take effect when
          purchased; downgrades take effect at the next renewal and may reduce the connectors, limits, and features available to
          you. You are responsible for adjusting your configuration to remain within a lower plan’s limits.
        </p>
      </>
    ),
  },
  {
    id: "taxes",
    group: "Billing and subscriptions",
    title: "Taxes",
    body: (
      <p>
        Prices are exclusive of taxes unless stated otherwise. You are responsible for all applicable sales, use, value-added,
        and similar taxes and government charges associated with your purchase, excluding taxes based on our net income. Where we
        are required to collect such taxes, they may be added to your charges.
      </p>
    ),
  },
  {
    id: "refunds",
    group: "Billing and subscriptions",
    title: "Refunds",
    body: (
      <p>
        Except where required by applicable law, all fees are non-refundable, and we do not provide refunds or credits for partial
        billing periods, unused time, downgrades, or features not used. Because you can evaluate FileAgent on the Free plan before
        purchasing, paid plans are sold on a no-refund basis; you may cancel at any time to stop future renewals as described
        above.
      </p>
    ),
  },
  {
    id: "nonpayment",
    group: "Billing and subscriptions",
    title: "Nonpayment, chargebacks, and license suspension",
    body: (
      <p>
        If a payment fails, is reversed, or is disputed (including chargebacks), or if you breach these Terms, we may suspend,
        limit, or terminate your license and access to paid features, in addition to our other remedies. We may require that any
        outstanding amounts be paid before reactivating a license.
      </p>
    ),
  },

  /* ---- Responsibilities & risk ---- */
  {
    id: "responsibilities",
    group: "Your responsibilities and risk",
    title: "Customer responsibilities",
    body: (
      <>
        <p>
          FileAgent is self-hosted software that you install and operate in your own environment. You are solely responsible for
          your deployment and for the systems FileAgent connects to, including:</p>
        <ul className="legal-list">
          <li>installing, configuring, securing, updating, and maintaining the FileAgent host and operating system;</li>
          <li>the accuracy and suitability of every job you create — sources, destinations, transfer modes, filters, file paths, patterns, schedules, retries, and conflict handling;</li>
          <li>the credentials, tokens, keys, certificates, and permissions used to reach source and destination systems;</li>
          <li>network reachability, firewall and port rules, DNS, storage capacity, and the reliability of the systems and links involved; and</li>
          <li>verifying that transfers, syncs, migrations, deletions, and other operations produced the results you intended.</li>
        </ul>
        <p>
          You are responsible for determining whether FileAgent is appropriate for your environment and for testing it before
          relying on it for production workflows.
        </p>
      </>
    ),
  },
  {
    id: "operational-risk",
    group: "Your responsibilities and risk",
    title: "File operations and operational risk",
    body: (
      <>
        <p>
          FileAgent moves, copies, synchronizes, mirrors, migrates, automates, and deletes files based entirely on the
          configuration you provide and the behavior of the systems you connect. The outcomes of these operations depend on
          factors outside our control.
        </p>
        <p>
          To the maximum extent permitted by law, FileAgent is not responsible or liable for, and you assume the risk of, any of
          the following:</p>
        <ul className="legal-list">
          <li>misconfigured jobs, incorrect file paths, incorrect file patterns, incorrect filters, or incorrect schedules;</li>
          <li>failed, duplicate, partial, delayed, interrupted, or out-of-order transfers;</li>
          <li>data loss, data corruption, or overwritten files at a source or destination, including corruption introduced by source or destination systems;</li>
          <li>network outages, latency, permission or authentication failures, capacity exhaustion, or storage failures;</li>
          <li>failures, outages, throttling, or changes by third-party systems, cloud providers, protocols, or APIs;</li>
          <li>credential compromise resulting from your actions, your environment, or your handling of secrets;</li>
          <li>incorrect reliance on logs, run history, reports, metrics, dashboards, alerts, or notifications; and</li>
          <li>business interruption, lost profits, or compliance failures arising from how you configure or use the Services.</li>
        </ul>
        <p>
          Logs, run history, and metrics are operational diagnostics and may be incomplete, delayed, or inaccurate in some
          conditions. You should independently verify outcomes for anything important.
        </p>
      </>
    ),
  },
  {
    id: "scheduled-purge",
    group: "Your responsibilities and risk",
    title: "Scheduled Purge and destructive operations",
    body: (
      <>
        <Callout tone="warn" title="Scheduled Purge permanently deletes files.">
          <p>
            Scheduled Purge and other delete, move, and sync/mirror operations can permanently and irreversibly remove files.
            Deletion is based on the age, pattern, and schedule you configure. FileAgent does not keep a separate copy of deleted
            files, and a purge is not a backup or a recycle bin. Once files are deleted, they may be unrecoverable.
          </p>
        </Callout>
        <p>
          You are solely responsible for the rules you configure and for their consequences. A pattern that matches more files
          than intended, an overly broad path, an incorrect age threshold, or a misconfigured schedule can delete data you did not
          intend to delete. We strongly recommend testing destructive jobs against non-production data and maintaining independent
          backups before enabling them.
        </p>
        <p>
          To the maximum extent permitted by law, FileAgent is not liable for any deletion, loss, or destruction of data caused by
          Scheduled Purge or any move, sync, mirror, or delete operation that results from your configuration, rules, patterns,
          schedules, or use of the Services.
        </p>
      </>
    ),
  },
  {
    id: "backups",
    group: "Your responsibilities and risk",
    title: "Backups and disaster recovery",
    body: (
      <p>
        You are responsible for maintaining your own backups and disaster-recovery processes for all source data, destination
        data, and your FileAgent host and configuration. Any backup, restore, or archive feature within FileAgent is a convenience
        for protecting FileAgent’s own configuration and is not a substitute for a comprehensive backup strategy for your files and
        systems. We are not responsible for your failure to maintain adequate backups.
      </p>
    ),
  },
  {
    id: "credentials",
    group: "Your responsibilities and risk",
    title: "Credentials, secrets, certificates, and keys",
    body: (
      <>
        <p>
          You provide and control the credentials, tokens, access keys, certificates, and other secrets that FileAgent uses to
          connect to your systems. FileAgent stores these secrets in encrypted form on your host and references them from job
          definitions; they are not transmitted to FileAgent as part of providing the Services.
        </p>
        <p>
          You are responsible for the validity, scope, rotation, and least-privilege configuration of these secrets, and for
          securing the FileAgent host and any backups that contain them. Because encrypted data is bound to protected material on
          the host, you are responsible for backing up and restoring that material together so your configuration remains usable.
        </p>
      </>
    ),
  },
  {
    id: "security-shared",
    group: "Your responsibilities and risk",
    title: "Shared security responsibilities",
    body: (
      <p>
        We use reasonable administrative, technical, and organizational safeguards for the website, licensing, and purchase
        systems we operate. Because FileAgent runs in your environment, the security of your deployment is your responsibility —
        including host hardening and patching, console access restrictions, network and firewall controls, credential management,
        user and role configuration, certificate management, and physical security. Security is a shared responsibility, and most
        incidents involving self-hosted software arise from environment configuration rather than the software itself.
      </p>
    ),
  },
  {
    id: "local-software",
    group: "Your responsibilities and risk",
    title: "Local installation, services, updates, and privileges",
    body: (
      <p>
        FileAgent installs as software and may run as a service on Windows or Linux and may require administrative or elevated
        operating-system privileges to install and operate. You are responsible for granting and managing those privileges
        appropriately and in line with your security policies. We may make updates available from time to time; you are responsible
        for applying updates in your environment, and we recommend applying security updates promptly. Older versions may be
        deprecated or unsupported over time.
      </p>
    ),
  },
  {
    id: "third-party",
    group: "Your responsibilities and risk",
    title: "Third-party services and integrations",
    body: (
      <p>
        FileAgent interoperates with third-party systems and protocols you choose to connect, such as SMB shares, FTP and SFTP
        servers, Amazon S3, Azure Blob Storage, and identity providers (for example, via SSO or LDAP). Your use of those systems
        is governed by your agreements with their providers. You are responsible for maintaining the necessary accounts,
        permissions, and security controls, and we are not responsible for the availability, performance, security, or changes of
        any third-party system, protocol, or API.
      </p>
    ),
  },

  /* ---- Use rules ---- */
  {
    id: "acceptable-use",
    group: "Acceptable use",
    title: "Acceptable use and prohibited conduct",
    body: (
      <>
        <p>You agree not to use the Services to, and not to permit anyone to:</p>
        <ul className="legal-list">
          <li>violate any law, regulation, sanction, export control, or third-party right, or facilitate any unlawful activity;</li>
          <li>transfer, store, or distribute malware, ransomware, or malicious, infringing, stolen, or unlawful content;</li>
          <li>access or move files without proper authorization from the owner of the source or destination system;</li>
          <li>infringe intellectual property, privacy, publicity, or confidentiality rights;</li>
          <li>interfere with, disrupt, or attempt to gain unauthorized access to any system, network, or data; or</li>
          <li>use the Services in any way that could harm FileAgent, other customers, or third parties, or that creates undue risk.</li>
        </ul>
        <p>
          You are responsible for the legality of the files you move and the systems you connect, and for obtaining all necessary
          rights, consents, and authorizations.
        </p>
      </>
    ),
  },
  {
    id: "regulated-data",
    group: "Acceptable use",
    title: "Regulated, sensitive, and high-risk use",
    body: (
      <>
        <p>
          Unless you and FileAgent have signed a separate written agreement that expressly permits it (for example, a Business
          Associate Agreement for protected health information), you must not use the Services to process or transfer:</p>
        <ul className="legal-list">
          <li>protected health information subject to HIPAA;</li>
          <li>payment card data subject to PCI DSS, beyond what is handled by our payment processor;</li>
          <li>government classified or controlled-but-unclassified information;</li>
          <li>export-controlled or sanctioned data or technology; or</li>
          <li>other data that carries heightened legal, regulatory, or contractual obligations.</li>
        </ul>
        <p>
          The Services are not designed or certified for high-risk environments where failure could lead to death, personal
          injury, or severe physical, environmental, or property damage. If you use the Services with any such data or in any such
          environment without our written authorization, you do so at your own risk, and you are solely responsible for any
          resulting obligations, claims, and consequences.
        </p>
      </>
    ),
  },
  {
    id: "export",
    group: "Acceptable use",
    title: "Export controls and sanctions",
    body: (
      <p>
        You must comply with all applicable export, re-export, sanctions, and trade-control laws. You represent that you are not
        located in, and will not use the Services in or for the benefit of, any country or party subject to applicable embargoes
        or restrictions, and that you are not a restricted or denied party under applicable law.
      </p>
    ),
  },

  /* ---- Rights & data ---- */
  {
    id: "customer-data",
    group: "Ownership and data",
    title: "Your content and data; limited license to process",
    body: (
      <p>
        As between you and FileAgent, you own your files, data, and configuration. Because FileAgent is self-hosted, your file
        contents move directly between your systems and are not transmitted to or stored by us. To the limited extent we process
        information in connection with the website, purchases, licensing, and support, we do so to provide and protect the
        Services and as described in our <a href="/privacy">Privacy Policy</a>. You grant us the limited rights necessary to
        provide those Services.
      </p>
    ),
  },
  {
    id: "feedback",
    group: "Ownership and data",
    title: "Feedback",
    body: (
      <p>
        If you give us suggestions, ideas, or feedback about the Services, you grant us a perpetual, irrevocable, worldwide,
        royalty-free license to use it for any purpose without restriction or obligation to you.
      </p>
    ),
  },
  {
    id: "ip",
    group: "Ownership and data",
    title: "Intellectual property and trademarks",
    body: (
      <p>
        FileAgent and its software, website, documentation, design, and the “FileAgent” name and logo are owned by FileAgent or
        its licensors and are protected by intellectual-property and other laws. Except for the limited license granted in these
        Terms, no rights are granted to you. You may not use our name, logo, or branding without our prior written permission.
      </p>
    ),
  },
  {
    id: "confidentiality",
    group: "Ownership and data",
    title: "Confidentiality",
    body: (
      <p>
        Non-public information that one party designates as confidential or that should reasonably be understood as confidential —
        including non-public pricing, license, security, and technical information — must be protected by the receiving party and
        used only as needed in connection with the Services, except where disclosure is required by law.
      </p>
    ),
  },

  /* ---- Service, changes, termination ---- */
  {
    id: "support",
    group: "Service, changes, and termination",
    title: "Support",
    body: (
      <p>
        Support is provided according to your plan and our then-current support practices. Except where a separate written
        agreement (such as an Enterprise plan) specifies committed response times or service levels, support is provided on a
        commercially reasonable, best-effort basis with no guaranteed response or resolution times. You can reach us at{" "}
        {SUPPORT_LINK}.
      </p>
    ),
  },
  {
    id: "availability",
    group: "Service, changes, and termination",
    title: "Changes, documentation, and availability",
    body: (
      <p>
        We may add, change, or remove features, documentation, plans, and limits over time, and we may perform maintenance on the
        website and licensing services. The FileAgent software runs on your host, so its availability depends on your environment.
        The website and licensing, activation, and purchase services are provided on an “as available” basis and may be temporarily
        unavailable. We are not liable for unavailability of these services or for any resulting impact on activation, updates, or
        purchases.
      </p>
    ),
  },
  {
    id: "termination",
    group: "Service, changes, and termination",
    title: "Suspension and termination",
    body: (
      <p>
        We may suspend or terminate your license or access to the Services if you breach these Terms, fail to pay, misuse the
        Services, or create risk or potential harm to FileAgent, other customers, or third parties. You may stop using the
        Services at any time. Cancelling a paid plan is described in the billing section above.
      </p>
    ),
  },
  {
    id: "effect-termination",
    group: "Service, changes, and termination",
    title: "Effect of termination",
    body: (
      <p>
        On termination or expiration, your license to use paid features ends and you must stop using any features you are no longer
        licensed to use. Because the software runs in your environment, your local installation, configuration, logs, and files
        remain under your control, and you are responsible for exporting, retaining, or deleting them. We may delete records we hold
        about your account, license, and purchases subject to the retention practices described in our{" "}
        <a href="/privacy">Privacy Policy</a> and our legal obligations. Sections that by their nature should survive termination
        will survive.
      </p>
    ),
  },

  /* ---- Legal terms ---- */
  {
    id: "warranties",
    group: "Legal terms",
    title: "Disclaimers of warranties",
    body: (
      <p>
        To the maximum extent permitted by law, the Services are provided “as is” and “as available,” without warranties of any
        kind, whether express, implied, or statutory, including implied warranties of merchantability, fitness for a particular
        purpose, title, non-infringement, and any warranties arising from course of dealing or usage. We do not warrant that the
        Services will be uninterrupted, error-free, secure, or free of data loss, that defects will be corrected, or that the
        Services will be compatible with every environment or meet your requirements. You are responsible for verifying results and
        maintaining backups.
      </p>
    ),
  },
  {
    id: "liability",
    group: "Legal terms",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, FileAgent and its owners, personnel, and suppliers will not be liable for any
          indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenue, data,
          files, goodwill, or business, or for business interruption, arising out of or relating to the Services, even if advised of
          the possibility of such damages and regardless of the theory of liability.
        </p>
        <p>
          To the maximum extent permitted by law, FileAgent’s total aggregate liability for all claims arising out of or relating to
          the Services will not exceed the greater of (a) the amounts you paid to FileAgent for the Services in the twelve (12)
          months before the event giving rise to the claim, or (b) one hundred U.S. dollars (US$100). These limitations apply even
          if a remedy fails of its essential purpose. Some jurisdictions do not allow certain limitations, so some of these may not
          apply to you.
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    group: "Legal terms",
    title: "Indemnification",
    body: (
      <p>
        You agree to defend, indemnify, and hold harmless FileAgent and its owners, personnel, and suppliers from and against any
        claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising out of or related to
        your configuration or use of the Services, your files and data, your systems and credentials, your violation of these Terms
        or applicable law, or your infringement of any third-party right.
      </p>
    ),
  },
  {
    id: "changes-terms",
    group: "Legal terms",
    title: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time. When we make material changes, we will update the effective date and version
        above, and where the change is significant we may ask you to accept the updated Terms (for example, before your next
        download). Your continued use of the Services after an update becomes effective constitutes acceptance of the updated Terms.
      </p>
    ),
  },
  {
    id: "governing-law",
    group: "Legal terms",
    title: "Governing law and venue",
    body: (
      <p>
        These Terms are governed by the laws of the State of Indiana, without regard to its conflict-of-laws rules, and excluding
        the U.N. Convention on Contracts for the International Sale of Goods. Subject to the dispute-resolution section below, you
        and FileAgent agree to the exclusive jurisdiction and venue of the state and federal courts located in the State of
        Indiana, and you consent to personal jurisdiction in those courts.
      </p>
    ),
  },
  {
    id: "disputes",
    group: "Legal terms",
    title: "Dispute resolution; jury and class-action waivers",
    body: (
      <>
        <p>
          Before filing a claim, you agree to first try to resolve the dispute informally by contacting us at {SUPPORT_LINK} and
          allowing at least thirty (30) days for good-faith resolution.
        </p>
        <p>
          To the extent permitted by law, you and FileAgent each waive any right to a jury trial in any proceeding arising out of
          or relating to these Terms or the Services. To the extent permitted by law, disputes must be brought in an individual
          capacity, and not as a plaintiff or class member in any purported class, collective, consolidated, or representative
          proceeding. Any claim must be brought within one (1) year after it arises, or it is permanently barred, to the extent
          permitted by law.
        </p>
      </>
    ),
  },
  {
    id: "force-majeure",
    group: "Legal terms",
    title: "Force majeure",
    body: (
      <p>
        We are not liable for any delay or failure to perform caused by events beyond our reasonable control, including acts of
        God, natural disasters, outages, network or infrastructure failures, third-party service failures, labor disputes, war,
        terrorism, government action, or other force-majeure events.
      </p>
    ),
  },
  {
    id: "assignment",
    group: "Legal terms",
    title: "Assignment",
    body: (
      <p>
        You may not assign or transfer these Terms or your license without our prior written consent. We may assign these Terms in
        connection with a merger, acquisition, reorganization, or sale of assets. Any attempted assignment in violation of this
        section is void.
      </p>
    ),
  },
  {
    id: "precedence",
    group: "Legal terms",
    title: "Severability, waiver, entire agreement, and order of precedence",
    body: (
      <>
        <p>
          If any provision of these Terms is held unenforceable, the remaining provisions remain in effect, and the unenforceable
          provision will be modified to the minimum extent necessary to make it enforceable. Our failure to enforce a provision is
          not a waiver of our right to do so later.
        </p>
        <p>
          These Terms, together with the Privacy Policy and any documents they expressly incorporate, are the entire agreement
          between you and FileAgent regarding self-serve use of the Services and supersede prior or contemporaneous understandings.
          In the event of a conflict, the following order of precedence applies: (1) a signed written agreement, order form, or
          enterprise agreement between you and FileAgent; (2) these Terms; and (3) the Privacy Policy.
        </p>
      </>
    ),
  },
  {
    id: "terms-contact",
    group: "Legal terms",
    title: "Contact",
    body: (
      <p>
        Questions about these Terms, licensing, or your account can be sent to {SUPPORT_LINK}. We are reachable by email for legal
        and contractual notices.
      </p>
    ),
  },
];

export function TermsPage() {
  return (
    <LegalLayout
      eyebrow="Terms of Service"
      title="FileAgent Terms of Service"
      summary="These Terms govern your use of FileAgent — the self-hosted file transfer automation software, the website, and the licensing, purchase, and support services. They set out your license, your responsibilities, how billing works, and how risk is shared, in plain terms."
      icon={<FileText />}
      intro={
        <Callout title="The short version (for convenience only — the full Terms below control).">
          <p>
            FileAgent runs on your own servers and moves your files between your systems. You control the jobs, credentials, and
            destinations, so you’re responsible for what you configure — especially deletions and Scheduled Purge, which are
            permanent. Paid plans are subscriptions that renew until you cancel. The software is provided “as is,” our liability is
            limited, and these Terms are governed by Indiana law.
          </p>
        </Callout>
      }
      sections={termsSections}
    />
  );
}

/* ================================================================== */
/* PRIVACY POLICY                                                      */
/* ================================================================== */

const privacySections: LegalSectionDef[] = [
  /* ---- Overview ---- */
  {
    id: "scope",
    group: "Overview",
    title: "Scope of this policy",
    body: (
      <p>
        This Privacy Policy explains how FileAgent (“FileAgent,” “we,” “us,” or “our”) handles information in connection with the
        FileAgent website, purchases, licensing and activation, and support (together, the “Services”). It also explains how the
        self-hosted FileAgent software is designed with respect to your data. It does not apply to third-party websites or systems
        you connect to FileAgent, which are governed by their own policies.
      </p>
    ),
  },
  {
    id: "self-hosted",
    group: "Overview",
    title: "Self-hosted design: what does not reach us",
    body: (
      <>
        <Callout title="Your file contents and credentials stay in your environment.">
          <p>
            FileAgent is self-hosted. Your files move directly between your own source and destination systems and are not
            transmitted to or stored by FileAgent. Your connection credentials and secrets are encrypted on your host and are not
            sent to us. The only routine outbound connection FileAgent makes to us is an encrypted (HTTPS) request for license
            validation and update checks.
          </p>
        </Callout>
        <p>
          As a result, the file paths, filenames, file contents, job logs, and audit events generated by your installation live on
          infrastructure you control — not on FileAgent’s systems — unless you separately and voluntarily send them to us (for
          example, by attaching logs to a support request).
        </p>
      </>
    ),
  },

  /* ---- Information we process ---- */
  {
    id: "info-you-provide",
    group: "Information we process",
    title: "Information you provide",
    body: (
      <>
        <p>When you purchase, license, or contact us, we process the information you give us, which may include:</p>
        <ul className="legal-list">
          <li><strong>Contact and account information</strong> — such as your name, email address, and (for sales inquiries) company name, role, and the details you submit in a contact form;</li>
          <li><strong>Billing information</strong> — handled by our payment processor, Stripe; we receive purchase records and limited billing metadata but not your full payment card number; and</li>
          <li><strong>Support communications</strong> — the messages, questions, and any files, logs, or screenshots you choose to send us when you request help.</li>
        </ul>
      </>
    ),
  },
  {
    id: "license-info",
    group: "Information we process",
    title: "License and activation information",
    body: (
      <p>
        To issue, validate, and support licenses, we process license identifiers and status, plan information, your billing email,
        and a customer identifier associated with your purchase. When your installation performs license validation or update
        checks, we may process the request and associated technical information needed to respond, such as license status and
        general request metadata.
      </p>
    ),
  },
  {
    id: "info-automatic",
    group: "Information we process",
    title: "Information collected automatically on the website",
    body: (
      <>
        <p>
          We use self-hosted Umami to measure website and documentation pageviews, referring sites, browser and device categories, approximate location, campaign labels, Web Vitals, and interactions such as pricing choices, download requests and installer-transfer starts, documentation searches and sales-form outcomes. Only allowlisted campaign parameters are collected; other URL queries, fragments, search text, form contents, license keys, and customer or payment identifiers are excluded. Anonymous licensing and update-check outcomes, billing-event categories, and gross paid amounts are measured separately from website visits. No card information is collected by analytics. Short-lived session storage retains campaign labels for up to 30 minutes without a visitor identifier. Tracking respects supported Do Not Track and Global Privacy Control signals.
          Website hosting providers may process technical request information to deliver and secure the service.
        </p>
      </>
    ),
  },
  {
    id: "info-installation",
    group: "Information we process",
    title: "Information inside your FileAgent installation",
    body: (
      <p>
        Your FileAgent installation generates and stores operational data on your own host to function — including job and transfer
        configuration, connection credentials (encrypted at rest), file paths, filenames, file sizes and timestamps, run history,
        logs, audit events, and error messages. This information resides on infrastructure you control and is governed by your own
        policies. We do not collect it as part of the Services, except to the extent you voluntarily share specific items with us
        (for example, in a support request).
      </p>
    ),
  },
  {
    id: "payment-info",
    group: "Information we process",
    title: "Payment information",
    body: (
      <p>
        Payments are processed by Stripe. Your payment details are collected and processed by Stripe under its
        own terms and privacy practices. We do not receive or store your full card number. We receive transaction
        results and billing metadata needed to manage your subscription and license.
      </p>
    ),
  },

  /* ---- Use & sharing ---- */
  {
    id: "how-we-use",
    group: "How we use and share information",
    title: "How we use information",
    body: (
      <>
        <p>We use the information described above to:</p>
        <ul className="legal-list">
          <li>provide, operate, and maintain the website, purchases, and licensing and activation services;</li>
          <li>process transactions, issue and validate licenses, and deliver updates;</li>
          <li>respond to your inquiries and provide support;</li>
          <li>secure the Services, prevent fraud and abuse, and enforce our Terms;</li>
          <li>understand and improve the website and the product; and</li>
          <li>comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information, and we do not use it for cross-context behavioral or targeted advertising.</p>
      </>
    ),
  },
  {
    id: "cookies",
    group: "How we use and share information",
    title: "Cookies and similar technologies",
    body: (
      <>
        <p>
          We do not use analytics, advertising, or cross-site tracking cookies.
          The site uses local storage for functional preferences, including acceptance of
          the current download Terms. Payment and licensing providers may use technologies needed for their services.
        </p>
      </>
    ),
  },
  {
    id: "how-we-share",
    group: "How we use and share information",
    title: "How we share information and our service providers",
    body: (
      <>
        <p>We share information only as needed to run the Services, including with:</p>
        <ul className="legal-list">
          <li><strong>Stripe</strong> — to process payments and manage subscriptions;</li>
          <li><strong>Railway</strong> — to host the website, documentation, and licensing services;</li>
          <li><strong>Cloudflare</strong> — to provide domain routing and website security; and</li>
          <li><strong>Infrastructure providers</strong> — email-delivery providers and other processors that help us operate the licensing services and send transactional emails.</li>
        </ul>
        <p>
          These providers process information on our behalf for the purposes described and are expected to protect it. We may also
          share information as described in the legal-disclosures and business-transfers sections below.
        </p>
      </>
    ),
  },
  {
    id: "legal-disclosures",
    group: "How we use and share information",
    title: "Legal and compliance disclosures",
    body: (
      <p>
        We may disclose information if we believe it is reasonably necessary to comply with a law, regulation, legal process, or
        governmental request; to enforce our Terms; to detect, prevent, or address fraud, security, or technical issues; or to
        protect the rights, property, or safety of FileAgent, our customers, or others.
      </p>
    ),
  },
  {
    id: "business-transfers",
    group: "How we use and share information",
    title: "Business transfers",
    body: (
      <p>
        If FileAgent is involved in a merger, acquisition, financing, reorganization, or sale of assets, information we hold may be
        transferred as part of that transaction. We will continue to protect the information and will provide notice where required
        by law.
      </p>
    ),
  },

  /* ---- Protection & retention ---- */
  {
    id: "security",
    group: "Protection and retention",
    title: "Security measures",
    body: (
      <p>
        We use reasonable administrative, technical, and organizational safeguards designed to protect the information we hold,
        including encryption in transit for the website and licensing services. No method of transmission or storage is completely
        secure, and we cannot guarantee absolute security. Because FileAgent is self-hosted, the security of your installation, host,
        credentials, and connected systems is your responsibility, as described in our <a href="/terms">Terms of Service</a>.
      </p>
    ),
  },
  {
    id: "retention",
    group: "Protection and retention",
    title: "Data retention",
    body: (
      <p>
        We retain information only as long as needed to operate and improve the Services and meet legal,
        accounting, and tax obligations. When information is no longer needed, we take reasonable steps
        to delete or de-identify it.
      </p>
    ),
  },
  {
    id: "international",
    group: "Protection and retention",
    title: "International users",
    body: (
      <p>
        FileAgent is operated from the United States, and the information we process for the website, purchases, licensing, and
        support is handled in the United States. If you access the Services from outside the United States, you understand that your
        information may be processed in the United States, where data-protection laws may differ from those in your location.
      </p>
    ),
  },

  /* ---- Rights & choices ---- */
  {
    id: "choices",
    group: "Your rights and choices",
    title: "Your choices and account controls",
    body: (
      <p>
        You can update your contact and billing details, cancel a subscription, or ask us to access or delete the personal
        information we hold by contacting us at {SUPPORT_LINK}. Because FileAgent is self-hosted, you directly control the user
        accounts, configuration, logs, and files within your own installation.
      </p>
    ),
  },
  {
    id: "state-rights",
    group: "Your rights and choices",
    title: "U.S. state privacy rights, including Indiana",
    body: (
      <>
        <p>
          Depending on where you live and whether the applicable law applies to us, you may have privacy rights under U.S. state
          laws, including the Indiana Consumer Data Protection Act. Where the law applies, these rights may include the right to:</p>
        <ul className="legal-list">
          <li>confirm whether we process your personal data and access that data;</li>
          <li>correct inaccuracies in your personal data;</li>
          <li>delete personal data you provided or that we obtained;</li>
          <li>obtain a portable copy of your personal data; and</li>
          <li>opt out of targeted advertising, the sale of personal data, or certain profiling — though we do not sell personal data or use it for targeted advertising.</li>
        </ul>
        <p>
          To exercise these rights, contact us at {SUPPORT_LINK}. We will respond within the time required by applicable law
          (generally within 45 days, with an extension where permitted) and may need to verify your identity. There is no charge for
          a reasonable request.
        </p>
        <p>
          <strong>Appeals.</strong> If we decline to act on your request, you may appeal by replying to our response or contacting us
          at {SUPPORT_LINK} with the subject “Privacy Appeal.” We will respond to your appeal as required by law. If your appeal is
          denied, you may contact the Indiana Attorney General to submit a complaint.
        </p>
      </>
    ),
  },
  {
    id: "dnt",
    group: "Your rights and choices",
    title: "Do Not Track and Global Privacy Control",
    body: (
      <p>
        Some browsers offer “Do Not Track” signals and opt-out preference signals such as Global Privacy Control (GPC). Because we
        do not sell personal information or use it for targeted advertising, these signals have limited effect, but where required
        by law we will treat a recognized opt-out preference signal as a valid request to opt out for that browser or device.
      </p>
    ),
  },
  {
    id: "children",
    group: "Your rights and choices",
    title: "Children’s privacy",
    body: (
      <p>
        The Services are intended for business and professional use by adults and are not directed to children. We do not knowingly
        collect personal information from anyone under 18. If you believe a child has provided us personal information, contact us
        at {SUPPORT_LINK} and we will take appropriate steps to delete it.
      </p>
    ),
  },
  {
    id: "breach",
    group: "Your rights and choices",
    title: "Security breach notification",
    body: (
      <p>
        If a security breach affecting personal information we hold occurs, we will investigate and provide notice to affected
        individuals and authorities as required by applicable law, including Indiana’s data-breach notification requirements,
        without unreasonable delay.
      </p>
    ),
  },
  {
    id: "changes-policy",
    group: "Your rights and choices",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. When we make material changes, we will update the effective date and
        version above. Your continued use of the Services after an update becomes effective constitutes acceptance of the updated
        policy.
      </p>
    ),
  },
  {
    id: "privacy-contact",
    group: "Your rights and choices",
    title: "Contact",
    body: (
      <p>
        For privacy questions or to exercise your rights, contact us at {SUPPORT_LINK}. We respond to privacy inquiries by email.
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalLayout
      updated={PRIVACY_UPDATED_DATE}
      eyebrow="Privacy Policy"
      title="FileAgent Privacy Policy"
      summary="This policy explains what information FileAgent collects for the website, purchases, licensing, and support — and why, because FileAgent is self-hosted, your file contents and credentials stay in your environment and never reach our systems."
      icon={<ShieldCheck />}
      intro={
        <Callout title="What matters most.">
          <p>
            FileAgent runs on your own servers, so your files move directly between your systems and are not sent to us. We process
            limited information to sell licenses, validate them, and support you — handled through providers like Stripe and our
            hosting providers. We don’t sell your personal information or use it for targeted advertising, and you have rights you
            can exercise at any time.
          </p>
        </Callout>
      }
      sections={privacySections}
    />
  );
}
